import React, { useState, useRef, useEffect } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { ChevronDown, ChevronUp, Sun, Moon, Coins } from "lucide-react";
import { cn } from "../../lib/utils";
import { BillingService } from "../../services/BillingService";
import { CreditBalance } from "../../model_gen/billing";
import { Skeleton } from "./skeleton";

// Base Dropdown Trigger
interface DropdownTriggerProps {
  children: React.ReactNode;
  onClick: () => void;
  isOpen: boolean;
  className?: string;
  disabled?: boolean;
}

export const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  children,
  onClick,
  isOpen,
  className,
  disabled = false,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center justify-between w-full px-3 py-2 text-sm",
        "bg-background border border-border rounded-md",
        "hover:bg-accent hover:text-accent-foreground",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "transition-colors duration-150",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}>
      <span className="flex-1 text-left">{children}</span>
      {isOpen ? (
        <ChevronUp className="w-4 h-4 ml-2" />
      ) : (
        <ChevronDown className="w-4 h-4 ml-2" />
      )}
    </button>
  );
};

// Dropdown Content Container
interface DropdownContentProps {
  children: React.ReactNode;
  isOpen: boolean;
  className?: string;
  align?: "left" | "right";
  openDirection?: "down" | "up";
}

export const DropdownContent: React.FC<DropdownContentProps> = ({
  children,
  isOpen,
  className,
  align = "left",
  openDirection = "down",
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute z-50 min-w-48",
        "bg-popover border border-border rounded-md shadow-lg",
        "py-1",
        openDirection === "up" ? "bottom-full mb-1" : "top-full mt-1",
        align === "right" ? "right-0" : "left-0",
        className
      )}>
      {children}
    </div>
  );
};

// Dropdown Item
interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  href?: string;
}

export const DropdownItem: React.FC<DropdownItemProps> = ({
  children,
  onClick,
  disabled = false,
  className,
  href,
}) => {
  const Component = href ? "a" : "button";

  return (
    <Component
      onClick={onClick}
      href={href}
      disabled={disabled}
      className={cn(
        "flex items-center w-full px-3 py-2 text-sm",
        "hover:bg-accent hover:text-accent-foreground",
        "focus:outline-none focus:bg-accent focus:text-accent-foreground",
        "transition-colors duration-150",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}>
      {children}
    </Component>
  );
};

// Dropdown Separator
export const DropdownSeparator: React.FC = () => {
  return <div className="my-1 border-t border-border" />;
};

// User Dropdown Component (specific for header)
interface UserDropdownProps {
  user?: {
    picture?: string;
    name?: string;
    email?: string;
  };
  onLogout: () => void;
  onNavigate: (path: string) => void;
  onSwitchOrganization?: () => void;
  theme?: "light" | "dark";
  onToggleTheme?: () => void;
  className?: string;
  apiKey?: ApiKeyOrGetter;
  showCredits?: boolean;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({
  user,
  onLogout,
  onNavigate,
  onSwitchOrganization,
  theme = "light",
  onToggleTheme,
  className,
  apiKey,
  showCredits = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loadingBalance, setLoadingBalance] = useState<boolean>(false);

  // Reset balance when apiKey changes (e.g., switching companies)
  useEffect(() => {
    setBalance(null);
  }, [apiKey]);

  // Fetch credit balance when dropdown opens
  useEffect(() => {
    if (isOpen && showCredits && apiKey && !balance) {
      setLoadingBalance(true);
      BillingService.getCreditBalance(null, apiKey).subscribe(
        (result: CreditBalance) => {
          setBalance(result);
          setLoadingBalance(false);
        },
        (error: any) => {
          console.error("Error fetching credit balance:", error);
          setLoadingBalance(false);
        }
      );
    }
  }, [isOpen, showCredits, apiKey, balance]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const handleThemeToggle = () => {
    if (onToggleTheme) {
      onToggleTheme();
    }
    // Don't close the dropdown when toggling theme
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getBalanceColor = (amount: number): string => {
    if (amount <= 0) return "text-destructive";
    return "text-foreground";
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-md hover:bg-accent transition-colors">
        {user?.picture ? (
          <img
            src={user.picture}
            alt="User avatar"
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <span className="text-sm font-medium">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </span>
          </div>
        )}
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      <DropdownContent isOpen={isOpen} align="right" className="min-w-56">
        {/* Credits Balance */}
        {showCredits && (
          <>
            <div className="px-3 py-2 border-b border-border">
              {loadingBalance ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Credits
                    </span>
                  </div>
                  <Skeleton className="h-5 w-20" />
                </div>
              ) : balance ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Credits
                    </span>
                  </div>
                  <div
                    className={cn(
                      "text-lg font-semibold",
                      getBalanceColor(balance.balance)
                    )}>
                    {formatCurrency(balance.balance, balance.currency)}
                  </div>
                  <button
                    onClick={() => {
                      onNavigate("/billing");
                      setIsOpen(false);
                    }}
                    className="w-full text-xs text-primary hover:underline text-left">
                    Add Credits →
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}

        {/* <DropdownItem disabled className="opacity-50">
          Account Settings
        </DropdownItem> */}
        <DropdownItem
          onClick={() => handleItemClick(() => onNavigate("/settings"))}>
          Settings
        </DropdownItem>
        <DropdownItem
          onClick={() => handleItemClick(() => onNavigate("/billing"))}>
          Billing
        </DropdownItem>
        <DropdownItem disabled className="opacity-50">
          Support
        </DropdownItem>
        {onSwitchOrganization && (
          <>
            <DropdownSeparator />
            <DropdownItem
              onClick={() => handleItemClick(onSwitchOrganization)}>
              Switch Organization
            </DropdownItem>
          </>
        )}
        {onToggleTheme && (
          <DropdownItem onClick={handleThemeToggle}>
            <div className="flex items-center justify-between w-full">
              <span>Theme</span>
              <div className="flex items-center gap-2 text-muted-foreground">
                {theme === "light" ? (
                  <>
                    <Sun className="w-4 h-4" />
                    <span className="text-xs">Light</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4" />
                    <span className="text-xs">Dark</span>
                  </>
                )}
              </div>
            </div>
          </DropdownItem>
        )}
        <DropdownItem onClick={() => handleItemClick(onLogout)}>
          Logout
        </DropdownItem>
      </DropdownContent>
    </div>
  );
};

// Company Switcher Component
interface CompanySwitcherProps {
  currentCompany: string;
  companies: { company_name: string; key_value: string; id?: number }[];
  onCompanyChange: (
    apiKey: ApiKeyOrGetter,
    companyName: string,
    companyId?: number
  ) => void;
  className?: string;
}

export const CompanySwitcher: React.FC<CompanySwitcherProps> = ({
  currentCompany,
  companies,
  onCompanyChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCompanySelect = (
    apiKey: ApiKeyOrGetter,
    companyName: string,
    companyId?: number
  ) => {
    onCompanyChange(apiKey, companyName, companyId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={cn("relative w-full", className)}>
      <DropdownTrigger
        onClick={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        className="w-full">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-maitai-green" />
          <span className="font-gc-med text-sm">{currentCompany}</span>
        </div>
      </DropdownTrigger>

      <DropdownContent isOpen={isOpen} className="w-full" openDirection="up">
        {companies.map((company) => (
          <DropdownItem
            key={company.key_value}
            onClick={() =>
              handleCompanySelect(
                company.key_value,
                company.company_name,
                company.id
              )
            }
            className={cn(
              currentCompany === company.company_name &&
                "bg-accent text-accent-foreground"
            )}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-maitai-green" />
              <span className="font-gc-med">{company.company_name}</span>
            </div>
          </DropdownItem>
        ))}
      </DropdownContent>
    </div>
  );
};
